import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { format, differenceInDays, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Search, Calendar, User, ToggleLeft, ToggleRight, Building2, AlertTriangle, Clock, Eye, X, CheckCircle2, FileText, ZoomIn, Loader2, Info } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';


interface VenueCampaign {
    id: string;
    campaign_name: string;
    start_date: string;
    end_date: string;
    ads_status: 'active' | 'inactive' | 'pending' | 'approved' | 'rejected' | 'ended' | 'correction';
    venue_partner_id: string;
    advertiser_user_id: string;
    media_ids?: string[];
    admin_comment?: string;
    venue_partner?: {
        venue_name: string;
        total_slots: number;
        available_slots: number;
    };
    advertiser_profile?: {
        full_name: string;
        company_name: string;
        email?: string;
        avatar_url?: string;
    };
}

const getStatusLabel = (status: string) => {
    switch (status) {
        case 'active': return 'Em veiculação';
        case 'approved': return 'Aprovada';
        case 'pending': return 'Pendente';
        case 'correction': return 'Correção';
        case 'rejected': return 'Rejeitada';
        case 'paused': return 'Pausada';
        case 'ended': return 'Finalizada';
        case 'inactive': return 'Inativo';
        default: return status;
    }
};

interface Media {
    id: string;
    title: string;
    url: string;
    type: string;
}

interface CampaignHistoryLog {
    id: string;
    new_status: string;
    admin_comment: string;
    created_at: string;
}


const VenueCampaignsSection: React.FC = () => {
    const [campaigns, setCampaigns] = useState<VenueCampaign[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [updatingId, setUpdatingId] = useState<string | null>(null);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    // Review Modal State
    const [isReviewOpen, setIsReviewOpen] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState<VenueCampaign | null>(null);
    const [previewMedias, setPreviewMedias] = useState<Media[]>([]);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [reviewStatus, setReviewStatus] = useState<string>('pending');
    const [reviewComment, setReviewComment] = useState('');
    const [historyLogs, setHistoryLogs] = useState<CampaignHistoryLog[]>([]);
    const [zoomedMedia, setZoomedMedia] = useState<Media | null>(null);


    const fetchCampaigns = async () => {
        setLoading(true);
        try {
            console.log('🔍 Buscando campanhas venue...');

            const { data, error } = await supabase
                .from('venue_campaigns')
                .select(`
          *,
          venue_partner:venue_partners (
            id,
            venue_name,
            total_slots,
            available_slots
          )
        `)
                .order('created_at', { ascending: false });

            if (error) throw error;

            // Buscar perfis dos anunciantes (manual join pois a relação pode não estar definida no client types)
            if (data) {
                const advertiserIds = [...new Set(data.map(c => c.advertiser_user_id).filter(Boolean))];

                if (advertiserIds.length > 0) {
                    const { data: profiles, error: profilesError } = await supabase
                        .from('profiles')
                        .select('id, full_name, company_name, avatar_url')
                        .in('id', advertiserIds);

                    if (!profilesError && profiles) {
                        // Map profiles to dictionary
                        const profileMap = profiles.reduce((acc, profile) => {
                            acc[profile.id] = profile;
                            return acc;
                        }, {} as Record<string, any>);

                        // Attach to campaigns
                        const enrichedData = data.map(campaign => ({
                            ...campaign,
                            advertiser_profile: profileMap[campaign.advertiser_user_id]
                        }));

                        setCampaigns(enrichedData);
                    } else {
                        setCampaigns(data);
                    }
                } else {
                    setCampaigns(data);
                }
            }
        } catch (error) {
            console.error('Erro ao buscar campanhas:', error);
            setMessage({ type: 'error', text: 'Erro ao carregar campanhas.' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCampaigns();
    }, []);

    const handleToggleStatus = async (campaign: VenueCampaign) => {
        const newStatus = campaign.ads_status === 'active' ? 'inactive' : 'active';
        const isActivating = newStatus === 'active';

        setUpdatingId(campaign.id);
        setMessage(null);

        try {
            // Se estiver ativando, verificar limites
            if (isActivating) {
                // Verificar capacidade atual
                // Buscar campanhas ativas neste estabelecimento
                const { count, error: countError } = await supabase
                    .from('venue_campaigns')
                    .select('*', { count: 'exact', head: true })
                    .eq('venue_partner_id', campaign.venue_partner_id)
                    .eq('ads_status', 'active');

                if (countError) throw countError;

                const activeCount = count || 0;
                const totalSlots = campaign.venue_partner?.total_slots || 0;

                if (activeCount >= totalSlots) {
                    setMessage({
                        type: 'error',
                        text: `Não é possível ativar. Limite de slots atingido (${activeCount}/${totalSlots}) neste estabelecimento.`
                    });
                    setUpdatingId(null);
                    return;
                }
            }

            // Atualizar status
            const { error: updateError } = await supabase
                .from('venue_campaigns')
                .update({ ads_status: newStatus })
                .eq('id', campaign.id);

            if (updateError) throw updateError;

            // Update local state
            setCampaigns(prev => prev.map(c =>
                c.id === campaign.id ? { ...c, ads_status: newStatus } : c
            ));

            setMessage({
                type: 'success',
                text: `Campanha ${isActivating ? 'ativada' : 'desativada'} com sucesso!`
            });

        } catch (error) {
            console.error('Erro ao atualizar status:', error);
            setMessage({ type: 'error', text: 'Erro ao atualizar status da campanha.' });
        } finally {
            setUpdatingId(null);
        }
    };

    const fetchPreviewMedias = async (mediaIds: string[]) => {
        if (!mediaIds || mediaIds.length === 0) {
            setPreviewMedias([]);
            return;
        }

        setLoadingPreview(true);
        try {
            const { data, error } = await supabase
                .from('medias')
                .select('*')
                .in('id', mediaIds);

            if (error) throw error;
            setPreviewMedias(data || []);
        } catch (err) {
            console.error('Erro ao buscar mídias:', err);
        } finally {
            setLoadingPreview(false);
        }
    };

    const fetchCampaignHistory = async (campaignId: string) => {
        try {
            const { data, error } = await supabase
                .from('venue_campaign_history')
                .select('*')
                .eq('campaign_id', campaignId)
                .order('created_at', { ascending: false });

            if (data) {
                setHistoryLogs(data);
            } else {
                setHistoryLogs([]); // Fallback
            }
        } catch (err) {
            console.log('Histórico não disponível ou tabela inexistente');
            setHistoryLogs([]);
        }
    };

    const handleOpenReview = (campaign: VenueCampaign) => {
        setSelectedCampaign(campaign);
        setReviewStatus(campaign.ads_status);
        setReviewComment(''); // Reset input to empty, clean state for new comments
        setIsReviewOpen(true);
        if (campaign.media_ids && campaign.media_ids.length > 0) {
            fetchPreviewMedias(campaign.media_ids);
        } else {
            setPreviewMedias([]);
        }
        fetchCampaignHistory(campaign.id);
    };

    const handleSaveReview = async () => {
        if (!selectedCampaign) return;

        setUpdatingId(selectedCampaign.id);
        try {
            // Se estiver ativando (mudando para active), verificar limites
            if (reviewStatus === 'active' && selectedCampaign.ads_status !== 'active') {
                const { count, error: countError } = await supabase
                    .from('venue_campaigns')
                    .select('*', { count: 'exact', head: true })
                    .eq('venue_partner_id', selectedCampaign.venue_partner_id)
                    .eq('ads_status', 'active');

                if (!countError) {
                    const activeCount = count || 0;
                    const totalSlots = selectedCampaign.venue_partner?.total_slots || 0;
                    if (activeCount >= totalSlots && totalSlots > 0) {
                        alert(`Não é possível ativar. Limite de slots atingido (${activeCount}/${totalSlots}). Aumente os slots ou desative outra campanha.`);
                        setUpdatingId(null);
                        return;
                    }
                }
            }

            const { error } = await supabase
                .from('venue_campaigns')
                .update({
                    ads_status: reviewStatus,
                    admin_comment: reviewComment?.trim()
                })
                .eq('id', selectedCampaign.id);

            if (error) throw error;

            // Update local state
            setCampaigns(prev => prev.map(c =>
                c.id === selectedCampaign.id
                    ? { ...c, ads_status: reviewStatus as any, admin_comment: reviewComment }
                    : c
            ));

            // Insert history log
            const { error: historyError } = await supabase.from('venue_campaign_history').insert({
                campaign_id: selectedCampaign.id,
                previous_status: selectedCampaign.ads_status,
                new_status: reviewStatus,
                admin_comment: reviewComment?.trim(),
                created_by: (await supabase.auth.getUser()).data.user?.id
            });

            if (historyError) {
                console.error("Error inserting history:", historyError);
                alert(`Erro ao salvar histórico: ${historyError.message}`); // Feedback visual para debug
            }

            // Refresh history
            fetchCampaignHistory(selectedCampaign.id);

            setMessage({ type: 'success', text: 'Campanha atualizada com sucesso!' });
            setReviewComment(''); // Reset input
            // setIsReviewOpen(false); // Mantendo aberto para ver o histórico atualizado conforme solicitado

        } catch (error) {
            console.error('Erro ao atualizar campanha:', error);
            setMessage({ type: 'error', text: 'Erro ao salvar alterações.' });
        } finally {
            setUpdatingId(null);
        }
    };

    // Filtrar campanhas
    const filteredCampaigns = campaigns.filter(campaign => {
        const query = searchQuery.toLowerCase();
        const advertiserName = campaign.advertiser_profile?.full_name?.toLowerCase() || '';
        const companyName = campaign.advertiser_profile?.company_name?.toLowerCase() || '';
        const campaignName = campaign.campaign_name?.toLowerCase() || '';
        const venueName = campaign.venue_partner?.venue_name?.toLowerCase() || '';

        return advertiserName.includes(query) ||
            companyName.includes(query) ||
            campaignName.includes(query) ||
            venueName.includes(query);
    });

    const getDurationDays = (start: string, end: string) => {
        try {
            const startDate = parseISO(start);
            const endDate = parseISO(end);
            return differenceInDays(endDate, startDate) + 1;
        } catch (e) {
            return 0;
        }
    };

    const getDayStatus = (start: string, end: string) => {
        try {
            const now = new Date();
            const startDate = parseISO(start);
            const endDate = parseISO(end);

            // Ajustar para comparar apenas datas, ignorando horas se necessário, mas aqui usaremos o padrão
            if (now < startDate) return 'Agendado';
            if (now > endDate) return 'Finalizado';
            return 'Em andamento';
        } catch (e) {
            return '-';
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-800">Campanhas Venue (Estabelecimentos)</h2>
                <div className="flex items-center space-x-2 bg-white rounded-md border shadow-sm px-3 py-2 w-full max-w-sm">
                    <Search className="h-5 w-5 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Buscar por anunciante, campanha ou local..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="flex-1 outline-none text-sm"
                    />
                </div>
            </div>

            {message && (
                <div className={`p-4 rounded-md flex items-center gap-2 ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
                    }`}>
                    {message.type === 'error' && <AlertTriangle className="h-5 w-5" />}
                    {message.text}
                </div>
            )}

            {loading ? (
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Anunciante / Campanha
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Estabelecimento (Venue)
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Período / Duração
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Ação / Revisão
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredCampaigns.length > 0 ? (
                                    filteredCampaigns.map((campaign) => (
                                        <tr key={campaign.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center">
                                                    <div className="flex-shrink-0 h-10 w-10 rounded-full overflow-hidden bg-indigo-100 flex items-center justify-center">
                                                        {campaign.advertiser_profile?.avatar_url ? (
                                                            <img
                                                                src={campaign.advertiser_profile.avatar_url}
                                                                alt={campaign.advertiser_profile.full_name || 'Anunciante'}
                                                                className="h-10 w-10 object-cover"
                                                            />
                                                        ) : (
                                                            <span className="text-indigo-600 font-bold">
                                                                {campaign.advertiser_profile?.full_name?.charAt(0) || campaign.campaign_name.charAt(0) || 'A'}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="ml-4">
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {campaign.advertiser_profile?.company_name || campaign.advertiser_profile?.full_name || 'Anunciante desconhecido'}
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            {campaign.campaign_name}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm text-gray-900 flex items-center gap-1">
                                                    <Building2 className="h-3 w-3 text-gray-400" />
                                                    {campaign.venue_partner?.venue_name || 'Local não encontrado'}
                                                </div>
                                                <div className="text-xs text-gray-500 mt-1">
                                                    Slots: {campaign.venue_partner?.available_slots ?? '?'} / {campaign.venue_partner?.total_slots ?? '?'}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col text-sm text-gray-900">
                                                    <span className="flex items-center gap-1">
                                                        <Calendar className="h-3 w-3 text-gray-400" />
                                                        {format(parseISO(campaign.start_date), 'dd/MM/yyyy')} - {format(parseISO(campaign.end_date), 'dd/MM/yyyy')}
                                                    </span>
                                                    <span className="flex items-center gap-1 text-gray-500 text-xs mt-1">
                                                        <Clock className="h-3 w-3" />
                                                        {getDurationDays(campaign.start_date, campaign.end_date)} dias ({getDayStatus(campaign.start_date, campaign.end_date)})
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${campaign.ads_status === 'active'
                                                    ? 'bg-green-100 text-green-800'
                                                    : campaign.ads_status === 'inactive'
                                                        ? 'bg-gray-100 text-gray-800'
                                                        : campaign.ads_status === 'rejected'
                                                            ? 'bg-red-100 text-red-800'
                                                            : campaign.ads_status === 'correction'
                                                                ? 'bg-orange-100 text-orange-800'
                                                                : 'bg-yellow-100 text-yellow-800'
                                                    }`}>
                                                    {campaign.ads_status === 'active' ? 'Ativo' :
                                                        campaign.ads_status === 'inactive' ? 'Inativo' :
                                                            campaign.ads_status === 'rejected' ? 'Rejeitada' :
                                                                campaign.ads_status === 'correction' ? 'Correção' :
                                                                    campaign.ads_status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex justify-end gap-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleOpenReview(campaign)}
                                                        className="text-gray-500 hover:text-indigo-600 p-1 h-8 w-8 rounded-full"
                                                        title="Revisar Mídia e Status"
                                                    >
                                                        <Eye className="h-5 w-5" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                                            <div className="flex flex-col items-center justify-center">
                                                <Search className="h-10 w-10 text-gray-300 mb-2" />
                                                <p>Nenhuma campanha encontrada com esse filtro.</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Review Modal */}
            <Dialog open={isReviewOpen} onOpenChange={setIsReviewOpen}>
                <DialogContent className="max-w-5xl h-[90vh] p-0 flex flex-col gap-0 overflow-hidden bg-white border-0 shadow-2xl">
                    <div className="px-6 py-4 border-b shrink-0 bg-white">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2 text-xl">
                                <Building2 className="h-6 w-6 text-orange-600" />
                                Revisão de Campanha Venue
                            </DialogTitle>
                            <DialogDescription className="text-gray-500">
                                Analise a mídia, verifique a lotação do local e defina o status.
                            </DialogDescription>
                        </DialogHeader>
                    </div>

                    {selectedCampaign && (
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 divide-x divide-gray-100 overflow-hidden">
                            {/* Left: Media Preview & History */}
                            <div className="h-full overflow-y-auto scrollbar-hide p-6 bg-gray-50/50 space-y-6">
                                <div className="space-y-3">
                                    <h4 className="font-semibold text-sm text-gray-700 flex items-center gap-2">
                                        <Eye className="h-4 w-4 text-orange-600" />
                                        Visualização da Mídia
                                    </h4>
                                    <div className="bg-white rounded-xl p-4 min-h-[300px] flex items-center justify-center border border-gray-200 shadow-sm">
                                        {loadingPreview ? (
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                                        ) : previewMedias.length > 0 ? (
                                            <div className="space-y-4 w-full">
                                                {previewMedias.map((media) => (
                                                    <div key={media.id} className="relative group rounded-lg overflow-hidden bg-black shadow-sm ring-1 ring-black/5">
                                                        {media.type === 'video' || media.url.match(/\.(mp4|webm)$/i) ? (
                                                            <video
                                                                src={media.url}
                                                                controls
                                                                className="w-full h-auto max-h-[350px]"
                                                            />
                                                        ) : (
                                                            <img
                                                                src={media.url}
                                                                alt={media.title}
                                                                className="w-full h-auto max-h-[350px] object-contain"
                                                            />
                                                        )}

                                                        {/* Zoom Overlay */}
                                                        <div
                                                            className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer z-10"
                                                            onClick={() => setZoomedMedia(media)}
                                                        >
                                                            <ZoomIn className="text-white h-10 w-10 drop-shadow-lg transform hover:scale-110 transition-transform" />
                                                        </div>

                                                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent text-white p-3 pt-6 text-xs z-20 pointer-events-none">
                                                            {media.title} ({media.type})
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-gray-400 text-sm flex flex-col items-center gap-2">
                                                <AlertTriangle className="h-8 w-8 opacity-20" />
                                                Nenhuma mídia encontrada
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* History Section */}
                                <div className="space-y-3">
                                    <h4 className="font-semibold text-sm text-gray-700 flex items-center gap-2">
                                        <FileText className="h-4 w-4 text-orange-600" />
                                        Histórico de Alterações
                                    </h4>
                                    <div className="bg-white border border-gray-200 rounded-xl p-0 overflow-hidden shadow-sm">
                                        <div className="max-h-[250px] overflow-y-auto scrollbar-hide p-4 space-y-4">
                                            {historyLogs.length > 0 ? historyLogs.map((log, index) => (
                                                <div key={log.id} className={`text-xs ${index !== historyLogs.length - 1 ? 'border-b border-gray-100 pb-3' : ''}`}>
                                                    <div className="flex justify-between items-center mb-1.5">
                                                        <span className="text-gray-400 font-medium">{format(parseISO(log.created_at), "dd/MM 'às' HH:mm")}</span>
                                                        <Badge variant="outline" className={`text-[10px] px-2 py-0.5 border h-5 ${log.new_status === 'active' ? 'text-green-600 border-green-200 bg-green-50' :
                                                            log.new_status === 'rejected' ? 'text-red-600 border-red-200 bg-red-50' :
                                                                log.new_status === 'correction' ? 'text-orange-600 border-orange-200 bg-orange-50' :
                                                                    'text-gray-600 bg-gray-50'
                                                            }`}>{log.new_status}</Badge>
                                                    </div>
                                                    <p className="text-gray-600 leading-relaxed bg-gray-50 p-2 rounded-lg mt-1">
                                                        {log.admin_comment || <span className="text-gray-300 italic">Sem comentário adicional</span>}
                                                    </p>
                                                </div>
                                            )) : (
                                                <div className="text-center py-8">
                                                    <p className="text-gray-400 text-xs">Nenhum registro de histórico.</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right: Details & Actions */}
                            <div className="h-full overflow-y-auto scrollbar-hide p-6 bg-white space-y-8">
                                <div className="bg-gradient-to-br from-orange-50 via-amber-50 to-white p-5 rounded-2xl border border-orange-100 shadow-sm">
                                    <div className="mb-4">
                                        <h3 className="font-bold text-xl text-gray-900 leading-tight">{selectedCampaign.campaign_name}</h3>
                                        <p className="text-sm text-gray-500 mt-1 font-medium">{selectedCampaign.advertiser_profile?.company_name || selectedCampaign.advertiser_profile?.full_name}</p>
                                    </div>

                                    <div className="grid grid-cols-2 gap-y-4 gap-x-6 text-sm">
                                        <div>
                                            <span className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Início</span>
                                            <span className="font-medium text-gray-700">{format(parseISO(selectedCampaign.start_date), 'dd/MM/yyyy')}</span>
                                        </div>
                                        <div>
                                            <span className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Término</span>
                                            <span className="font-medium text-gray-700">{format(parseISO(selectedCampaign.end_date), 'dd/MM/yyyy')}</span>
                                        </div>
                                        <div className="col-span-2">
                                            <span className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Local (Venue)</span>
                                            <span className="font-medium text-gray-800 flex items-center gap-1.5">
                                                <Building2 className="h-4 w-4 text-orange-500" />
                                                {selectedCampaign.venue_partner?.venue_name}
                                            </span>
                                        </div>
                                        <div className="col-span-2 pt-2 border-t border-orange-100 mt-2">
                                            <div className="flex justify-between items-center">
                                                <span className="block text-xs font-semibold text-gray-400 uppercase tracking-wider">Capacidade do Local</span>
                                                <Badge className={`${(selectedCampaign.venue_partner?.available_slots || 0) > 0 ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 disabled:opacity-50' : 'bg-red-100 text-red-700'} border-0`}>
                                                    {selectedCampaign.venue_partner?.available_slots ?? '?'} vagas disponíveis
                                                </Badge>
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                                                <div
                                                    className="bg-gradient-to-r from-orange-400 to-amber-500 h-1.5 rounded-full"
                                                    style={{ width: `${Math.min(100, (((selectedCampaign.venue_partner?.total_slots || 1) - (selectedCampaign.venue_partner?.available_slots || 0)) / (selectedCampaign.venue_partner?.total_slots || 1)) * 100)}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-6">
                                    <div className="space-y-3">
                                        <Label htmlFor="status" className="text-sm font-semibold text-gray-700">Status da Campanha</Label>
                                        <Select value={reviewStatus} onValueChange={setReviewStatus}>
                                            <SelectTrigger className="w-full h-11 bg-gray-50 border-gray-200 hover:border-orange-300 focus:ring-orange-200 transition-all">
                                                <SelectValue placeholder="Selecione o status" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="pending">
                                                    <div className="flex items-center gap-2">
                                                        <span className="h-2 w-2 rounded-full bg-yellow-500 shadow-[0_0_8px_rgba(234,179,8,0.5)]" />
                                                        Pendente
                                                    </div>
                                                </SelectItem>
                                                <SelectItem value="active">
                                                    <div className="flex items-center gap-2">
                                                        <span className="h-2 w-2 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)]" />
                                                        Em veiculação (Ativo)
                                                    </div>
                                                </SelectItem>
                                                <SelectItem value="correction">
                                                    <div className="flex items-center gap-2">
                                                        <span className="h-2 w-2 rounded-full bg-orange-500 shadow-[0_0_8px_rgba(249,115,22,0.5)]" />
                                                        Solicitar Correção
                                                    </div>
                                                </SelectItem>
                                                <SelectItem value="rejected">
                                                    <div className="flex items-center gap-2">
                                                        <span className="h-2 w-2 rounded-full bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]" />
                                                        Não Aprovada (Rejeitada)
                                                    </div>
                                                </SelectItem>
                                                <SelectItem value="paused">
                                                    <div className="flex items-center gap-2">
                                                        <span className="h-2 w-2 rounded-full bg-gray-500" />
                                                        Pausada
                                                    </div>
                                                </SelectItem>
                                                <SelectItem value="ended">
                                                    <div className="flex items-center gap-2">
                                                        <span className="h-2 w-2 rounded-full bg-gray-400" />
                                                        Finalizada
                                                    </div>
                                                </SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="space-y-3">
                                        <Label htmlFor="comment" className="text-sm font-semibold text-gray-700 flex justify-between">
                                            Comentário / Feedback
                                            <span className="text-[10px] font-normal text-gray-400 uppercase tracking-wider">Visível para o anunciante</span>
                                        </Label>
                                        <Textarea
                                            id="comment"
                                            placeholder="Digite uma mensagem para o anunciante (ex: motivo da reprovação ou confirmação de ativação)..."
                                            value={reviewComment}
                                            onChange={(e) => setReviewComment(e.target.value)}
                                            className="min-h-[120px] bg-gray-50 border-gray-200 focus:border-orange-300 focus:ring-orange-200 resize-none p-3 text-sm"
                                        />
                                        <div className="flex items-start gap-2 bg-orange-50 p-2 rounded-lg text-orange-700 text-xs">
                                            <Info className="h-4 w-4 shrink-0 mt-0.5" />
                                            <p>Este comentário será enviado ao cliente e ficará registrado no histórico da campanha.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="px-6 py-4 border-t bg-gray-50 shrink-0 flex justify-end gap-3">
                        <Button variant="outline" onClick={() => setIsReviewOpen(false)} className="border-gray-300">
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleSaveReview}
                            disabled={updatingId !== null}
                            className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-md hover:shadow-lg transition-all"
                        >
                            {updatingId ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Salvando...
                                </>
                            ) : (
                                <>
                                    <CheckCircle2 className="mr-2 h-4 w-4" />
                                    Salvar
                                </>
                            )}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Media Zoom Dialog */}
            <Dialog open={!!zoomedMedia} onOpenChange={() => setZoomedMedia(null)}>
                <DialogContent className="max-w-[90vw] max-h-[90vh] bg-black border-gray-800 p-0 overflow-hidden flex items-center justify-center outline-none">
                    <div className="relative w-full h-full flex items-center justify-center">
                        <Button
                            className="absolute top-4 right-4 z-50 rounded-full bg-black/50 hover:bg-black/70 text-white border border-white/20"
                            size="icon"
                            onClick={() => setZoomedMedia(null)}
                        >
                            <X className="h-6 w-6" />
                        </Button>
                        {zoomedMedia && (
                            zoomedMedia.type === 'video' || zoomedMedia.url.match(/\.(mp4|webm)$/i) ? (
                                <video
                                    src={zoomedMedia.url}
                                    controls
                                    autoPlay
                                    className="max-w-full max-h-[90vh] w-auto h-auto outline-none"
                                />
                            ) : (
                                <img
                                    src={zoomedMedia.url}
                                    alt={zoomedMedia.title}
                                    className="max-w-full max-h-[90vh] w-auto h-auto object-contain"
                                />
                            )
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default VenueCampaignsSection;
